/* OTP — O que falta ao teu look.
   Autoplay is a hand-rolled eased scroll rather than scrollTo({behavior:'smooth'}),
   which is quick and abrupt; this drifts over ~1.1s and always lands on a card. */

class OtpLook extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.track = this.querySelector('[data-track]');
    this.cards = Array.from(this.querySelectorAll('[data-card]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    if (!this.track || !this.cards.length) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.delay = (parseFloat(this.dataset.speed) || 4.5) * 1000;
    this.paused = false;
    this.visible = false;

    this.bindControls();
    this.track.addEventListener('scroll', () => this.sync(), { passive: true });
    window.addEventListener('resize', () => this.sync());
    this.sync();

    if (this.dataset.autoplay !== 'false' && !this.reduced) this.watch();
  }

  /* geometry */
  get step() {
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return this.cards[0].getBoundingClientRect().width + gap;
  }

  get maxScroll() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  sync() {
    // one dot per card in the markup; how many are reachable depends on how many
    // cards fit, which only the browser knows
    const last = Math.round(this.maxScroll / this.step);
    const i = Math.min(last, Math.round(this.track.scrollLeft / this.step));
    this.dots.forEach((dot, k) => {
      dot.hidden = k > last;
      dot.setAttribute('aria-current', k === i ? 'true' : 'false');
    });
    const prev = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    if (prev) prev.disabled = this.track.scrollLeft <= 2;
    if (next) next.disabled = this.track.scrollLeft >= this.maxScroll - 2;
  }

  /* an eased scroll of our own: smooth, and it always settles on a card edge */
  glide(to, ms) {
    const from = this.track.scrollLeft;
    const target = Math.min(Math.max(to, 0), this.maxScroll);
    if (Math.abs(target - from) < 1) return;
    if (this.reduced) {
      this.track.scrollLeft = target;
      return;
    }
    cancelAnimationFrame(this.raf);
    const t0 = performance.now();
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const frame = (now) => {
      const t = Math.min(1, (now - t0) / ms);
      this.track.scrollLeft = from + (target - from) * ease(t);
      if (t < 1) this.raf = requestAnimationFrame(frame);
    };
    this.raf = requestAnimationFrame(frame);
  }

  advance() {
    const at = this.track.scrollLeft;
    const next = at >= this.maxScroll - 2 ? 0 : Math.round(at / this.step) * this.step + this.step;
    this.glide(next, next === 0 ? 1400 : 1100);
  }

  bindControls() {
    const prev = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    if (prev) prev.addEventListener('click', () => this.nudge(-1));
    if (next) next.addEventListener('click', () => this.nudge(1));

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.hold();
        this.glide(i * this.step, 700);
      });
    });

    // any hint of the visitor taking over stops the drift for a while
    ['pointerenter', 'pointerdown', 'touchstart', 'wheel', 'focusin'].forEach((ev) => {
      this.addEventListener(ev, () => this.hold(), { passive: true });
    });
    this.addEventListener('pointerleave', () => {
      this.paused = false;
    });
  }

  nudge(dir) {
    this.hold();
    this.glide(Math.round(this.track.scrollLeft / this.step) * this.step + dir * this.step, 700);
  }

  hold() {
    this.paused = true;
    clearTimeout(this.resume);
    this.resume = setTimeout(() => {
      this.paused = false;
    }, 6000);
  }

  /* only runs while the section is actually on screen */
  watch() {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        this.visible = e.isIntersecting;
      }),
      { threshold: 0.25 }
    );
    io.observe(this);

    this.timer = setInterval(() => {
      if (this.paused || !this.visible || document.hidden) return;
      if (this.maxScroll < 2) return;
      this.advance();
    }, this.delay);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    cancelAnimationFrame(this.raf);
  }
}

if (!customElements.get('otp-look')) customElements.define('otp-look', OtpLook);
